// S23 — a tenant's agents and the model profile each runs on (spec 15 §4 S23,
// §5; spec 14 §3.10; D-645). Every staff role reads the list and the profiles
// this environment offers; a platform_admin (a sandbox admin only on a sandbox
// tenant) switches an agent's profile behind a confirm step that says what the
// switch costs: a live Bedrock profile spends tokens against the agent's
// daily budget and the account's monthly Bedrock budget, and `recorded` costs
// nothing and drafts nothing it has no recording for.
//
// The server stays the fence. A refused switch keeps the dialog open with the
// reason in words; a refusal that means the screen is stale (a version
// conflict, an unavailable model, an agent gone) reads the list again.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Modal } from "../../../components/modal";
import { CardSkeleton, EmptyState, ErrorState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { enumLabel } from "../../../lib/enum-label";
import { joinSentences } from "../../../lib/sentences";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { TenantTabs } from "../components/tenant-tabs";
import {
  getTenantAgents,
  setAgentModelProfile,
  type AgentModelDeployment,
  type AgentModelProfile,
  type TenantAgent,
} from "../lib/agents-api";
import {
  canSwitchModel,
  dailyCeilingMicroUsd,
  formatUsd,
  refusalRereads,
  switchRefusal,
  switchTargets,
  type SwitchRefusal,
} from "../lib/agents";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { getTenant, PlatformError, type PlatformMe } from "../lib/platform-api";
import { tenantQueryKey } from "./tenant-detail";

export function tenantAgentsQueryKey(id: string) {
  return ["platform", "tenant", id, "agents"] as const;
}

function useAgentName() {
  const { t } = useTranslation();
  return (agent: Pick<TenantAgent, "display_name" | "agent_code">): string =>
    agent.display_name !== null && agent.display_name.trim().length > 0
      ? agent.display_name
      : enumLabel(t, "agents.catalog", `${agent.agent_code}.name`);
}

export function TenantAgentsScreen({ me, tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: ({ signal }) => getTenant(tenantId, signal),
  });
  const agents = useQuery({
    queryKey: tenantAgentsQueryKey(tenantId),
    queryFn: ({ signal }) => getTenantAgents(tenantId, signal),
  });
  useSignInAgainOn401(detail.error ?? agents.error);
  const [editing, setEditing] = useState<TenantAgent | null>(null);
  const [switched, setSwitched] = useState<{ agent: string; profile: string } | null>(null);
  const agentName = useAgentName();
  const crumbs = [{ label: t("admin.tenant.back"), to: "/console/tenants" }];

  if (detail.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-agents-loading">
        <PageHeader title={t("admin.agentModels.loading")} crumbs={crumbs} />
        <CardSkeleton lines={5} label={t("admin.agentModels.loading")} />
      </section>
    );
  }
  if (detail.isError) {
    const notFound =
      detail.error instanceof PlatformError &&
      (detail.error.status === 404 || detail.error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-agents-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={crumbs}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
            }}
            requestId={requestIdOf(detail.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }

  const tenant = detail.data.tenant;
  const writable = canSwitchModel(me, tenant.sandbox);
  // A platform_admin the sandbox fence holds back is told why (D-477 ruling 3).
  const fencedAdmin = !writable && me.role === "platform_admin" && me.scope === "sandbox";

  let body;
  if (agents.isPending) {
    body = <CardSkeleton lines={5} label={t("admin.agentModels.loading")} />;
  } else if (agents.isError) {
    body = (
      <ErrorState
        title={t("admin.agentModels.loadFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void agents.refetch();
        }}
        requestId={requestIdOf(agents.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    const { deployment, items } = agents.data;
    const columns: DataTableColumn<TenantAgent>[] = [
      {
        key: "agent",
        header: t("admin.agentModels.colAgent"),
        id: true,
        phonePriority: 1,
        render: (a) => agentName(a),
      },
      {
        key: "status",
        header: t("admin.agentModels.colStatus"),
        kind: "status",
        render: (a) => (
          <StatusChip variant={a.status === "enabled" ? "ok" : "neutral"}>
            {t(`admin.agentModels.status.${a.status}`)}
          </StatusChip>
        ),
      },
      {
        key: "profile",
        header: t("admin.agentModels.colProfile"),
        phonePriority: 2,
        render: (a) => <ProfileCell name={a.model_profile} deployment={deployment} />,
      },
      {
        key: "budget",
        header: t("admin.agentModels.colBudget"),
        kind: "qty",
        phonePriority: 3,
        render: (a) => new Intl.NumberFormat(i18n.language).format(a.daily_token_budget),
      },
      ...(writable
        ? [
            {
              key: "action",
              header: t("admin.agentModels.colAction"),
              actions: true,
              render: (a: TenantAgent) => (
                <Button
                  size="sm"
                  data-testid={`admin-agent-change-${a.agent_code}`}
                  disabled={switchTargets(deployment, a.model_profile).length === 0}
                  onClick={() => {
                    setSwitched(null);
                    setEditing(a);
                  }}
                >
                  {t("admin.agentModels.change")}
                </Button>
              ),
            } satisfies DataTableColumn<TenantAgent>,
          ]
        : []),
    ];
    body = (
      <>
        {deployment.bedrock_configured ? null : (
          <Banner kind="neutral" data-testid="admin-agents-no-bedrock">
            <strong>{t("admin.agentModels.noBedrockTitle")}</strong>{" "}
            {joinSentences(
              [t("admin.agentModels.noBedrockBody"), t("admin.agentModels.noBedrockFix")],
              i18n.language,
            )}
          </Banner>
        )}
        {switched === null ? null : (
          <Banner kind="ok" data-testid="admin-agents-switched">
            {t("admin.agentModels.switched", switched)}
          </Banner>
        )}
        <Card title={t("admin.agentModels.title")} data-testid="admin-agents-list">
          <Card.Body>
            {items.length === 0 ? (
              <EmptyState
                title={t("admin.agentModels.emptyTitle")}
                body={t("admin.agentModels.emptyBody")}
              />
            ) : (
              <DataTable
                columns={columns}
                rows={items}
                rowKey={(a) => a.id}
                empty={null}
                caption={t("admin.agentModels.title")}
                rowTestId="admin-agent-row"
                rowProps={(a) => ({ "data-agent": a.agent_code, "data-profile": a.model_profile })}
              />
            )}
            {writable ? null : fencedAdmin ? (
              <p className="admin-hint" data-testid="admin-agents-sandbox-note">
                {t("admin.agentModels.sandboxNote")}
              </p>
            ) : (
              <p className="admin-hint" data-testid="admin-agents-viewer-note">
                {t("admin.agentModels.viewerNote")}
              </p>
            )}
          </Card.Body>
        </Card>
        <Card title={t("admin.agentModels.profilesTitle")} data-testid="admin-agents-profiles">
          <Card.Body>
            <p className="admin-hint">
              {deployment.default_profile === null
                ? t("admin.agentModels.noDefault")
                : t("admin.agentModels.defaultProfile", { name: deployment.default_profile })}
            </p>
            <ul className="admin-agent-profiles">
              {deployment.profiles.map((p) => (
                <li key={p.name} data-testid={`admin-agents-profile-${p.name}`}>
                  <ProfileFacts profile={p} />
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
        {editing === null ? null : (
          <SwitchDialog
            tenantId={tenantId}
            agent={editing}
            agentName={agentName(editing)}
            deployment={deployment}
            onClose={() => {
              setEditing(null);
            }}
            onSwitched={(agent) => {
              setEditing(null);
              setSwitched({ agent: agentName(agent), profile: agent.model_profile });
            }}
          />
        )}
      </>
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-agents">
      <PageHeader title={tenant.name} crumbs={crumbs} />
      <TenantTabs tenantId={tenantId} />
      <p className="admin-card-lead">
        {joinSentences(
          [
            t("admin.agentModels.lead"),
            t("admin.agentModels.leadRecorded"),
            t("admin.agentModels.leadLive"),
          ],
          i18n.language,
        )}
      </p>
      {body}
    </section>
  );
}

function ProfileCell({ name, deployment }: { name: string; deployment: AgentModelDeployment }) {
  const { t } = useTranslation();
  const offered = deployment.profiles.find((p) => p.name === name);
  return (
    <span className="admin-agent-profile" data-testid="admin-agent-profile">
      <code>{name}</code>
      <span className="admin-muted">
        {offered === undefined
          ? t("admin.agentModels.adapter.other")
          : t(`admin.agentModels.adapter.${offered.adapter}`)}
      </span>
    </span>
  );
}

function ProfileFacts({ profile }: { profile: AgentModelProfile }) {
  const { t, i18n } = useTranslation();
  const input = profile.input_micro_usd_per_million_tokens;
  const output = profile.output_micro_usd_per_million_tokens;
  return (
    <>
      <span className="admin-agent-profile">
        <code>{profile.name}</code>
        <span className="admin-muted">{t(`admin.agentModels.adapter.${profile.adapter}`)}</span>
      </span>
      <span className="admin-hint">
        {input === null || output === null
          ? t("admin.agentModels.noPrice")
          : t("admin.agentModels.price", {
              input: formatUsd(input, i18n.language),
              output: formatUsd(output, i18n.language),
            })}
      </span>
      <StatusChip
        variant={
          profile.availability === "ok"
            ? "ok"
            : profile.availability === "unavailable"
              ? "bad"
              : "neutral"
        }
      >
        {profile.availability === "unavailable"
          ? t("admin.agentModels.availability.unavailable", {
              reason: profile.unavailable_reason ?? "",
            })
          : t(`admin.agentModels.availability.${profile.availability}`)}
      </StatusChip>
    </>
  );
}

function SwitchDialog({
  tenantId,
  agent,
  agentName,
  deployment,
  onClose,
  onSwitched,
}: {
  tenantId: string;
  agent: TenantAgent;
  agentName: string;
  deployment: AgentModelDeployment;
  onClose: () => void;
  onSwitched: (agent: TenantAgent) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const targets = switchTargets(deployment, agent.model_profile);
  const [choice, setChoice] = useState<string>(targets[0]?.name ?? "");
  const [refusal, setRefusal] = useState<SwitchRefusal | null>(null);
  const held = useHeldKey();
  const target = targets.find((p) => p.name === choice);
  const write = useMutation({
    mutationFn: (body: { model_profile: string; version: number }) =>
      withHeldKey(held, body, (key) => setAgentModelProfile(tenantId, agent.id, body, key)),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: tenantAgentsQueryKey(tenantId) });
      onSwitched(updated);
    },
    onError: async (error) => {
      const why = switchRefusal(error);
      setRefusal(why);
      if (refusalRereads(why)) {
        await queryClient.invalidateQueries({ queryKey: tenantAgentsQueryKey(tenantId) });
      }
    },
  });
  useSignInAgainOn401(write.error);

  const ceiling =
    target === undefined ? null : dailyCeilingMicroUsd(agent.daily_token_budget, target);
  const budget = new Intl.NumberFormat(i18n.language).format(agent.daily_token_budget);

  return (
    <Modal
      label={t("admin.agentModels.dialogTitle")}
      heading={t("admin.agentModels.dialogTitle")}
      closeLabel={t("admin.agentModels.cancel")}
      onClose={onClose}
      pending={write.isPending}
      testId="admin-agent-switch"
      className="admin-modal"
    >
      <p className="admin-card-lead">
        {t("admin.agentModels.dialogLead", { agent: agentName, current: agent.model_profile })}
      </p>
      {targets.length === 0 ? (
        <p className="admin-hint">{t("admin.agentModels.noTargets")}</p>
      ) : (
        <fieldset className="admin-packs">
          <legend>{t("admin.agentModels.choose")}</legend>
          {targets.map((p) => (
            <label
              key={p.name}
              className={choice === p.name ? "admin-pack is-selected" : "admin-pack"}
              data-testid={`admin-agent-switch-option-${p.name}`}
            >
              <input
                type="radio"
                name="model-profile"
                value={p.name}
                checked={choice === p.name}
                onChange={() => {
                  setChoice(p.name);
                  setRefusal(null);
                }}
              />
              <span className="admin-pack-name">{p.name}</span>
              <span className="admin-pack-desc">{t(`admin.agentModels.adapter.${p.adapter}`)}</span>
            </label>
          ))}
        </fieldset>
      )}
      {target === undefined ? null : target.adapter === "bedrock" ? (
        <Banner kind="warn" data-testid="admin-agent-switch-cost">
          <strong>{t("admin.agentModels.costTitle")}</strong>{" "}
          {joinSentences(
            [
              t("admin.agentModels.costRuns", { profile: target.name }),
              ceiling === null
                ? ""
                : t("admin.agentModels.costBudget", {
                    budget,
                    ceiling: formatUsd(ceiling, i18n.language),
                  }),
              t("admin.agentModels.costAccount"),
            ],
            i18n.language,
          )}
        </Banner>
      ) : (
        <Banner kind="neutral" data-testid="admin-agent-switch-free">
          {joinSentences(
            [
              t("admin.agentModels.freeRuns", { profile: target.name }),
              t("admin.agentModels.freeGap"),
            ],
            i18n.language,
          )}
        </Banner>
      )}
      <p className="admin-hint">{t("admin.agentModels.effect")}</p>
      {refusal === null ? null : (
        <Banner kind="bad" data-testid="admin-agent-switch-refused" data-refusal={refusal}>
          {t(`admin.agentModels.refusal.${refusal}`)}
        </Banner>
      )}
      <div className="admin-actions">
        <Button onClick={onClose} disabled={write.isPending}>
          {t("admin.agentModels.cancel")}
        </Button>
        <Button
          variant="primary"
          busy={write.isPending}
          busyLabel={t("admin.agentModels.saving")}
          disabled={target === undefined || refusal === "conflict" || refusal === "gone"}
          data-testid="admin-agent-switch-apply"
          onClick={() => {
            if (target === undefined) return;
            setRefusal(null);
            write.mutate({ model_profile: target.name, version: agent.version });
          }}
        >
          {target === undefined
            ? t("admin.agentModels.change")
            : t("admin.agentModels.confirm", { profile: target.name })}
        </Button>
      </div>
    </Modal>
  );
}
